import {
  Component, Input, Output, EventEmitter, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef, HostListener, OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Label, CreateLabelDto } from '../../models/label.model';
import { LabelService } from '../../services/label.service';
import type { ApplicationLabelsTranslations } from '../../i18n/application-labels';
import en from '../../i18n/en/application-labels';

const PRESET_COLORS = [
  '#EF4444', '#F59E0B', '#F97316', '#84CC16',
  '#10B981', '#14B8A6', '#3B82F6', '#6366F1',
  '#8B5CF6', '#EC4899', '#6B7280', '#475569',
];

const DROPDOWN_WIDTH = 260;

@Component({
  selector: 'app-application-labels',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './application-labels.component.html',
  styleUrls: ['./application-labels.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationLabelsComponent implements OnDestroy {
  @Input() applicationId!: number;
  @Input() assignedLabels: any[] = [];
  @Input() translations: ApplicationLabelsTranslations = en;
  @Output() labelsChanged = new EventEmitter<any[]>();
  @ViewChild('addTrigger', { static: false }) addTriggerRef!: ElementRef<HTMLElement>;

  allLabels: any[] = [];
  dropdownOpen = false;
  dropdownTop = 0;
  dropdownLeft = 0;
  searchQuery = '';
  creating = false;
  newLabelName = '';
  newLabelColor = '#6366F1';
  saving = false;
  presetColors = PRESET_COLORS;

  private activeCard: HTMLElement | null = null;

  constructor(
    private labelService: LabelService,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.resetCardZIndex();
  }

  private ensureLoaded(): void {
    if (this.allLabels.length === 0) {
      this.loadLabels();
    }
  }

  loadLabels(): void {
    this.labelService.getLabels().subscribe({
      next: (res) => {
        this.allLabels = res.items;
        this.cdr.markForCheck();
      },
    });
  }

  get filteredAvailable(): any[] {
    const assigned = new Set(this.assignedLabels.map((l: any) => l.id));
    const available = this.allLabels.filter((l: any) => !assigned.has(l.id));
    if (!this.searchQuery.trim()) return available;
    const q = this.searchQuery.toLowerCase();
    return available.filter((l: any) => l.name.toLowerCase().includes(q));
  }

  private setCardZIndex(): void {
    const card = this.el.nativeElement.closest('.application-card') as HTMLElement;
    if (card) {
      card.style.zIndex = '100';
      this.activeCard = card;
    }
  }

  private resetCardZIndex(): void {
    if (this.activeCard) {
      this.activeCard.style.zIndex = '';
      this.activeCard = null;
    }
  }

  private positionDropdown(): void {
    requestAnimationFrame(() => {
      const trigger = this.addTriggerRef?.nativeElement;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow >= 340 || spaceBelow >= spaceAbove) {
        this.dropdownTop = rect.bottom + 6;
      } else {
        this.dropdownTop = rect.top - 6;
      }
      this.dropdownLeft = Math.min(rect.left, window.innerWidth - DROPDOWN_WIDTH - 16);
      this.cdr.markForCheck();
    });
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.searchQuery = '';
      this.creating = false;
      this.ensureLoaded();
      this.setCardZIndex();
      this.positionDropdown();
    } else {
      this.resetCardZIndex();
    }
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.resetCardZIndex();
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.dropdownOpen) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.label-dropdown') && !target.closest('.add-label-trigger')) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(): void {
    if (this.dropdownOpen) this.closeDropdown();
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    if (this.dropdownOpen) this.positionDropdown();
  }

  @HostListener('window:resize', [])
  onResize(): void {
    if (this.dropdownOpen) this.positionDropdown();
  }

  assignLabel(label: any): void {
    if (this.assignedLabels.some((l: any) => l.id === label.id)) return;
    this.labelService.assignLabel(this.applicationId, label.id).subscribe({
      next: () => {
        this.assignedLabels = [...this.assignedLabels, label];
        this.labelsChanged.emit(this.assignedLabels);
        this.closeDropdown();
        this.cdr.markForCheck();
      },
      error: (err) => {
        if (err?.status === 409) {
          this.assignedLabels = [...this.assignedLabels, label];
          this.labelsChanged.emit(this.assignedLabels);
          this.closeDropdown();
          this.cdr.markForCheck();
        }
      },
    });
  }

  removeLabel(label: any): void {
    this.labelService.unassignLabel(this.applicationId, label.id).subscribe({
      next: () => {
        this.assignedLabels = this.assignedLabels.filter((l: any) => l.id !== label.id);
        this.labelsChanged.emit(this.assignedLabels);
        this.cdr.markForCheck();
      },
    });
  }

  startCreate(): void {
    this.creating = true;
    this.newLabelName = '';
    this.newLabelColor = '#6366F1';
  }

  cancelCreate(): void {
    this.creating = false;
  }

  saveNewLabel(): void {
    if (!this.newLabelName.trim() || this.saving) return;
    this.saving = true;
    const dto: CreateLabelDto = { name: this.newLabelName.trim(), color: this.newLabelColor };
    this.labelService.createLabel(dto).subscribe({
      next: (label: Label) => {
        this.allLabels = [...this.allLabels, label];
        this.saving = false;
        this.creating = false;
        this.cdr.markForCheck();
        this.assignLabel(label);
      },
      error: () => {
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  getLabelDisplayName(label: any): string {
    if (label.i18n_key && this.translations) {
      const key = label.i18n_key.replace(/\./g, '_');
      return (this.translations as any)[key] || label.name;
    }
    return label.name;
  }

  t(key: string, fallback: string): string {
    return (this.translations as any)?.[key] || fallback;
  }
}
